import { Metamodel } from '../base/model';
import { LinkTopic } from '../synapselink/linktopic';
import { AdapterRegistry } from './adapter-registry';
import { NamedAsyncAdapter } from './named-async.adapter';
import { A365BackendModel as Model } from '../a365.backendmodel';
import { ReferenceAdapter } from './reference.adapter';
import { LinkedService } from '../linkedservice/linkedservice';
import { SharedModel } from '../sharedmodel';
import { ValueAdapter } from './value.adapter';
import _ from 'lodash';
import { Constants } from '../../shared/shared-constants';
import { linkedServiceReferenceToResource } from '../../shared/utils/linkedservice-parameter.util';
import { convertParametersToModel } from '../../shared/utils/parameter-convert.util';
import { Owned } from '../base/named';
import { ParameterValue } from '../base/parameter-value';
import { ModelConstants } from '../model-constants';
import { LakehouseLinkedService } from '../linkedservice/lakehouse.linkedservice';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(LinkTopic),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            folder: '*',
            description: '*',
            sourceDatabase: {
                linkedService: '*',
                typeProperties: {
                    resourceId: '*',
                    principalId: '*'
                }
            },
            targetDatabase: {
                linkedService: '*',
                typeProperties: {
                    crossTableTransaction: '*',
                    dropExistingTargetTableOnStart: '*',
                    actionOnExistingTargetTable: '*',
                    vacuum: '*',
                    optimizedWrite: '*',
                    autoCompact: '*'
                }
            },
            landingZone: {
                linkedService: '*',
                sasToken: 'secret',
                fileSystem: '*',
                folderPath: '*',
                encryptedCredential: '*'
            },
            compute: {
                coreCount: '*',
                computeType: '*',
                dataProcessIntervalInMinutes: '*'
            }
        }
    }
})
export class LinkTopicAdapter extends NamedAsyncAdapter<LinkTopic, Model.LinkTopicResource> {
    protected override async _toResource(linkTopic: LinkTopic): Promise<Model.LinkTopicResource> {
        const result = (await super._toResource(linkTopic)) as Model.LinkTopicResource;
        const properties = this.properties(result);
        properties.sourceDatabase = {
            typeProperties: {
                resourceId: linkTopic.resourceId,
                principalId: linkTopic.principalId
            }
        };
        properties.targetDatabase = {
            typeProperties: {
                crossTableTransaction: linkTopic.crossTableTransaction,
                actionOnExistingTargetTable: linkTopic.actionOnExistingTargetTable
            }
        };
        if (linkTopic.landingZoneToken || linkTopic.landingZoneFileSystem) {
            const sourceType = Metamodel.modelOf(linkTopic.sourceLinkedService)?.name;
            const passToken: boolean =
                linkTopic.landingZoneToken && sourceType && sourceType !== ModelConstants.sapCdcLinkedService;
            properties.landingZone = {
                fileSystem: linkTopic.landingZoneFileSystem,
                folderPath: linkTopic.landingZoneFolder,
                sasToken: passToken
                    ? {
                        type: 'SecureString',
                        value: linkTopic.landingZoneToken
                    }
                    : undefined
            };
        }
        properties.compute = {
            coreCount: linkTopic.computeCoreCount,
            computeType: linkTopic.computeType,
            dataProcessIntervalInMinutes: linkTopic.dataProcessIntervalInMinutes
        };
        if (linkTopic.sourceLinkedService) {
            properties.sourceDatabase.linkedService = linkedServiceReferenceToResource(
                linkTopic.sourceLinkedService,
                this.context,
                undefined,
                this.ensureLogger()
            );
        }
        if (linkTopic.targetLinkedService) {
            linkTopic.targetLinkedServiceParameters = [Owned.create(ParameterValue.MODEL, linkTopic)];
            linkTopic.targetLinkedServiceParameters[0].parameter = Array.from(
                linkTopic.targetLinkedService.parameters
            )[0];
            linkTopic.targetLinkedServiceParameters[0].valueType = 'string';
            linkTopic.targetLinkedServiceParameters[0].value =
                linkTopic.targetDatabaseName && linkTopic.targetSqlPoolName
                    ? `${linkTopic.targetDatabaseName}@${linkTopic.targetSqlPoolName}`
                    : linkTopic.targetDatabaseName;
            properties.targetDatabase.linkedService = linkedServiceReferenceToResource(
                linkTopic.targetLinkedService,
                this.context,
                linkTopic.targetLinkedServiceParameters,
                this.ensureLogger()
            );

            if (linkTopic.targetLinkedService instanceof LakehouseLinkedService) {
                properties.targetDatabase.typeProperties.vacuum = linkTopic.vacuum;
                properties.targetDatabase.typeProperties.optimizedWrite = linkTopic.optimizedWrite;
                properties.targetDatabase.typeProperties.autoCompact = linkTopic.autoCompact;
            }
        }
        if (linkTopic.landingZoneLinkedService) {
            properties.landingZone.linkedService = linkedServiceReferenceToResource(
                linkTopic.landingZoneLinkedService,
                this.context,
                undefined,
                this.ensureLogger()
            );
        }
        if (linkTopic.raw) {
            const parsedRaw = JSON.parse(linkTopic.raw);
            _.merge(result, parsedRaw);
        }
        return result;
    }

    protected override async _toModel(
        linkTopicResource: Model.LinkTopicResource,
        linkTopic?: LinkTopic
    ): Promise<LinkTopic> {
        if (!linkTopic) {
            linkTopic = LinkTopic.MODEL.create();
        }
        await super._toModel(linkTopicResource, linkTopic);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const properties = linkTopicResource.properties || ({} as any);
        if (properties.sourceDatabase) {
            const sourceDatabase = properties.sourceDatabase;
            linkTopic.resourceId = sourceDatabase.typeProperties.resourceId;
            linkTopic.principalId = sourceDatabase.typeProperties.principalId;
            if (sourceDatabase.linkedService) {
                linkTopic.sourceLinkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                    Metamodel.modelFor(LinkedService),
                    sourceDatabase.linkedService, this.context
                );
            }
        }
        if (properties.targetDatabase && properties.targetDatabase.linkedService) {
            const targetLinkedServiceResource = properties.targetDatabase.linkedService;
            linkTopic.targetLinkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                Metamodel.modelFor(LinkedService),
                targetLinkedServiceResource, this.context
            );
            if ((targetLinkedServiceResource as SharedModel.LinkedServiceReference).parameters) {
                // Parameter value for linked service
                convertParametersToModel(
                    linkTopic,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                    linkTopic.targetLinkedService,
                    linkTopic.targetLinkedServiceParameters,
                    (targetLinkedServiceResource as SharedModel.LinkedServiceReference).parameters
                );
                if (linkTopic.targetLinkedServiceParameters && linkTopic.targetLinkedServiceParameters.length) {
                    const [dbName, poolName] = linkTopic.targetLinkedServiceParameters[0].value.split('@');
                    linkTopic.targetSqlPoolName = poolName;
                    linkTopic.targetDatabaseName = dbName;
                }
            }
        }
        if (properties.landingZone) {
            const landingZone = properties.landingZone;
            linkTopic.landingZoneFileSystem = landingZone.fileSystem;
            linkTopic.landingZoneFolder = landingZone.folderPath;
            linkTopic.landingZoneToken = landingZone.sasToken?.value || Constants.maskedCredential;
            linkTopic.landingZoneLinkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                Metamodel.modelFor(LinkedService),
                landingZone.linkedService, this.context
            );
        }
        if (properties.compute) {
            linkTopic.computeType = properties.compute.computeType;
            linkTopic.computeCoreCount = properties.compute.coreCount;
            linkTopic.dataProcessIntervalInMinutes = properties.compute.dataProcessIntervalInMinutes;
        }

        linkTopic.crossTableTransaction = properties?.targetDatabase?.typeProperties?.crossTableTransaction;
        const dropExistingTargetTableOnStart =
            properties?.targetDatabase?.typeProperties?.dropExistingTargetTableOnStart;
        const actionOnExistingTargetTable = properties?.targetDatabase?.typeProperties?.actionOnExistingTargetTable;
        linkTopic.actionOnExistingTargetTable =
            actionOnExistingTargetTable != null
                ? actionOnExistingTargetTable
                : dropExistingTargetTableOnStart != null
                    ? dropExistingTargetTableOnStart
                        ? 'DropAndRecreateTable'
                        : 'FailOnNonEmptyTable'
                    : undefined;
        linkTopic.vacuum = properties?.targetDatabase?.typeProperties?.vacuum;
        linkTopic.optimizedWrite = properties?.targetDatabase?.typeProperties?.optimizedWrite;
        linkTopic.autoCompact = properties?.targetDatabase?.typeProperties?.autoCompact;
        linkTopic.raw = JSON.stringify(await ValueAdapter.remainder(this.context, linkTopicResource, LinkTopic.MODEL));

        return linkTopic;
    }
}
